/**
 * Tilläggsavtal om tidsbestämd hyra — tillaggsavtal_tidsbestamd_hyra v1
 * (Formulär R-22).
 *
 * Connected to bostadshyra + studentbostad: its own contract row and its
 * own signing (both parties, plus a medhyresgäst when one is named).
 * Parties, property, inflyttningshyra, the parent avtalsnummer and the
 * officiellt lägenhetsnummer are inherited from the parent lease.
 */
import { applyDefaults, COMPOSITION_SCHEMA, COMPOSITION_VERSION } from '@hyranai/regta-document-ir';
import { declaredSectionInstances } from '../common/index.js';
import { TILLAGGSAVTAL_TIDSBESTAMD_HYRA_BLOCKS } from '../tillaggsavtal_tidsbestamd_hyra/blocks.js';
const K = 'tillaggsavtal_tidsbestamd_hyra';
const TITLE = 'Tilläggsavtal om tidsbestämd hyra';
const FORM_NUMBER = 'Formulär R-22';
const SLOTS = TILLAGGSAVTAL_TIDSBESTAMD_HYRA_BLOCKS.flatMap((b) => b.slots);
export const tillaggsavtalTidsbestamdHyraV1 = (values = {}) => ({
    schema: COMPOSITION_SCHEMA,
    version: COMPOSITION_VERSION,
    documentKey: 'tillaggsavtal_tidsbestamd_hyra',
    documentVersion: 'v1',
    title: TITLE,
    locale: 'sv-SE',
    meta: {
        category: 'Bostadshyra',
        numberStyle: 'paragraph',
        shortTitle: 'Tidsbestämd hyra',
        formNumber: FORM_NUMBER,
        preamble: 'Tillägg till bostadshyresavtalet om en på förhand bestämd hyresutveckling (tidsbestämd hyra).',
        description: 'Tillägg till ett bostadshyresavtal (även studentbostad) om tidsbestämd hyra: en på förhand överenskommen hyresutveckling där hyran justeras ett bestämt datum varje år med en angiven höjningsprocent.\n\n' +
            'Vem undertecknar: hyresvärden och hyresgästen. Skickas via Scrive.\n\n' +
            'Kopplad mall — skapas under det bostadshyresavtal som tillägget avser. Parter och inflyttningshyra förförifylls från avtalet. Ange hyresperioden och en rad per justering (datum + höjningsprocent); den beloppsbestämda månadshyran i kronor räknas ut automatiskt.',
        status: 'active',
        signingMode: 'digital',
        signingPlacementAnchors: 'per-party-label',
        connectedTo: ['bostadshyra', 'studentbostad'],
        inheritFromParent: {
            parties: 'by-role-ordinal',
            property: true,
            values: [
                { key: 'move_in_rent', fromParentKey: 'rent_amount' },
                { key: 'parent_contract_number', fromParentKey: 'contract_number' },
                { key: 'apartment_official_number', fromParentKey: 'property_official_apt_no' },
            ],
        },
        partySlots: [
            { id: 'lessor', label: 'Hyresvärd', role: 'lessor', ordinal: 0, required: true, signs: true },
            { id: 'lessee_primary', label: 'Hyresgäst', role: 'lessee', ordinal: 0, required: true, signs: true },
            { id: 'lessee_co', label: 'Medhyresgäst', role: 'lessee', ordinal: 1, signs: true, help: 'Valfritt — lägg till om avtalet har två hyresgäster.' },
        ],
        propertySlots: [{ id: 'property', label: 'Fastighet', multi: false, required: true }],
    },
    values: applyDefaults({ fields: SLOTS }, values),
    blocks: [
        { instanceId: 'letterhead', blockRef: 'common.letterhead@v1', slot: 'letterhead', input: { title: TITLE, form_number: FORM_NUMBER } },
        { instanceId: 'footer', blockRef: 'common.page-footer@v1', slot: 'footer', input: { form_number: FORM_NUMBER } },
        { instanceId: 'heading', blockRef: 'common.document-heading@v1', input: { title: TITLE } },
        { instanceId: 'avtalsnummer', blockRef: `${K}.avtalsnummer@v1`, input: {} },
        ...declaredSectionInstances([
            { ref: 'hyresvard', blockRef: `${K}.hyresvard@v1` },
            { ref: 'hyresgast', blockRef: `${K}.hyresgast@v1`, besidePrevious: true },
            { ref: 'hyresavtalet', blockRef: `${K}.hyresavtalet@v1` },
            { ref: 'tidsbestamd-hyra', blockRef: `${K}.tidsbestamd-hyra@v1` },
            {
                ref: 'underskrifter',
                blockRef: `${K}.underskrifter@v1`,
                input: {
                    ref: 'underskrifter',
                    title: 'Underskrifter',
                    parties: [
                        { label: 'Hyresvärd', role: 'lessor', ordinal: 0 },
                        { label: 'Hyresgäst', role: 'lessee', ordinal: 0 },
                        { label: 'Medhyresgäst', role: 'lessee', ordinal: 1, when_set: 'cotenant_name' },
                    ],
                },
            },
        ], { customBlockRef: `${K}.custom-sections@v1` }),
    ],
});
//# sourceMappingURL=tillaggsavtal_tidsbestamd_hyra.js.map